import type { CatalogCompetency, ValidationCriteria } from "./catalog";
import type { InteractiveCompetencyCheckpoint as Checkpoint, InteractiveQuestionBlock, InteractiveActivityBlock } from "./interactive-activity";
import type { InteractiveActivityPackage as Package } from "./interactive-activity-package";

type EvidenceReferences = { evidenceBlockIds?: string[]; questionIds?: string[] };
type AnchorReference = { positionAfterBlockId?: string; positionAfterQuestionId?: string };
export const checkpointEvidenceIds = (c: EvidenceReferences) => c.evidenceBlockIds ?? c.questionIds ?? [];
export const checkpointAnchorId = (c: AnchorReference) => c.positionAfterBlockId ?? c.positionAfterQuestionId ?? "";
export const isEvidenceBlock = (b: InteractiveActivityBlock): b is Extract<InteractiveActivityBlock, {type: "question" | "measurement_table" | "graph_task"}> =>
  b.type === "question" || b.type === "measurement_table" || b.type === "graph_task";
export const checkpointBlocks = (pkg: Package) => pkg.interactive.blocks.filter(isEvidenceBlock);
export function normalizeCheckpoint(c: Checkpoint) {
  const {questionIds, positionAfterQuestionId, ...rest} = c;
  return {...rest, evidenceBlockIds: [...checkpointEvidenceIds(c)], ...(checkpointAnchorId(c) ? {positionAfterBlockId: checkpointAnchorId(c)} : {})};
}
export const checkpointQuestions = (pkg: Package) => pkg.interactive.blocks.filter((b): b is InteractiveQuestionBlock => b.type === "question");
export function questionLabel(pkg: Package, id: string) {
  const questions = checkpointQuestions(pkg);
  const index = questions.findIndex(q => q.id === id);
  return pkg.interactive.blocks.find(b => b.id === id)?.title || (index < 0 ? id : `Q${index + 1}`);
}
export function suggestedCheckpointQuestions(pkg: Package, competencyId: string, afterId: string) {
  const questions = pkg.interactive.blocks;
  const end = questions.findIndex(q => q.id === afterId);
  if (end < 0) return [];
  const previous = (pkg.interactive.competencyCheckpoints ?? [])
    .filter(c => c.competencyId === competencyId)
    .map(c => questions.findIndex(q => q.id === checkpointAnchorId(c)))
    .filter(index => index >= 0 && index <= end);
  const start = previous.length ? Math.max(...previous) + 1 : 0;
  return questions.slice(start, end + 1).filter(q => isEvidenceBlock(q) && q.competencyIds?.includes(competencyId)).map(q => q.id);
}
export function newCheckpoint(competencyId: string, afterId: string, questionIds: string[]): Checkpoint {
  return {
    id: `checkpoint-${crypto.randomUUID()}`, competencyId,
    questionIds: [...questionIds], positionAfterQuestionId: afterId,
    evaluationMode: "manual",
    studentDisplay: { showCompetenceName: true, showCriteria: true, showGradeBeforeValidation: false },
    feedback: { enabled: true, automatic: false },
  };
}
export function checkpointCriteria(checkpoint: Checkpoint, catalog: CatalogCompetency[]): ValidationCriteria | undefined {
  return checkpoint.rubric ?? catalog.find(c => c.id === checkpoint.competencyId)?.criteria;
}

/** Example uses the real catalogue IDs and references its criteria, never copies them. */
export function checkpointExample(catalog: CatalogCompetency[], schoolLevel: string): Package {
  const ana = catalog.find(c => c.schoolLevel === schoolLevel && c.code === "ANA3");
  const rea = catalog.find(c => c.schoolLevel === schoolLevel && c.code.replace("É", "E") === "REA2");
  if (!ana || !rea) throw new Error("L’exemple nécessite ANA3 et RÉA2 dans le référentiel de ce niveau.");
  const prompts = [
    "Proposez une hypothèse sur l’effet de la vitesse d’une source sur le son perçu.",
    "Prévoyez ce qui change lorsque la source se rapproche du récepteur.",
    "Précisez une observation qui permettrait de vérifier votre hypothèse.",
    "Décrivez le montage que vous avez réalisé pour observer le phénomène.",
    "Indiquez les vérifications réalisées pour obtenir un montage fonctionnel.",
  ];
  return {
    format: "competenceo-interactive-activity", version: 1,
    activity: {title:"Exemple · checkpoints de compétences",schoolLevel,theme:"Ondes et signaux",chapter:"Preuves et évaluation",coefficient:1,competencyIds:[ana.id,rea.id]},
    interactive: {schemaVersion:1,revision:1,status:"draft",flow:"linear",assets:[],competencyRules:[],
      blocks:prompts.map((prompt,i)=>({id:`q${i+1}`,type:"question",answerType:"open",prompt,correctionMode:"teacher",competencyIds:[i<3?ana.id:rea.id]})),
      competencyCheckpoints:[
        {...newCheckpoint(ana.id,"q3",["q1","q2","q3"]), id:"checkpoint_ana3_01"},
        {...newCheckpoint(rea.id,"q5",["q4","q5"]), id:"checkpoint_rea2_01"},
      ],
    },
  };
}
